import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { adminService } from '../services/adminService';
import { AdminClubDetail, AdminClubListItem } from '../types';

const formatAmount = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const formatToggle = (value: boolean): string => (value ? 'SIM' : 'NAO');

const ClubsAdminPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [clubs, setClubs] = useState<AdminClubListItem[]>([]);
  const [detail, setDetail] = useState<AdminClubDetail | null>(null);
  const [selectedClubId, setSelectedClubId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailError, setDetailError] = useState('');

  const loadData = useCallback(async (nextSearch = '') => {
    setLoading(true);
    setError('');

    try {
      const payload = await adminService.listClubs(nextSearch);
      setClubs(payload);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar clubs');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (clubId: string) => {
    setSelectedClubId(clubId);
    setDetailLoading(true);
    setDetailError('');

    try {
      const payload = await adminService.getClubDetail(clubId);
      setDetail(payload);
    } catch (nextError: any) {
      setDetail(null);
      setDetailError(nextError.message || 'Erro ao carregar detalhe do club');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData('');
  }, [loadData]);

  const metricCards = useMemo(
    () =>
      detail
        ? [
            {
              eyebrow: 'Players',
              title: 'Online agora',
              value: String(detail.summary.online_players),
              tone: 'emerald' as const,
            },
            {
              eyebrow: 'Mesas',
              title: 'Ativas',
              value: String(detail.summary.active_tables),
              tone: 'blue' as const,
            },
            {
              eyebrow: 'Rake',
              title: 'Ultimos 30 dias',
              value: formatAmount(detail.summary.rake_30d),
              tone: 'amber' as const,
            },
            {
              eyebrow: 'Depositos',
              title: 'Ultimos 30 dias',
              value: formatAmount(detail.summary.deposits_30d),
              tone: 'emerald' as const,
            },
            {
              eyebrow: 'Saques',
              title: 'Fila aberta',
              value: String(detail.summary.pending_withdrawals_count),
              tone: 'rose' as const,
            },
            {
              eyebrow: 'Saldo',
              title: 'Carteira rastreada',
              value: formatAmount(detail.summary.tracked_balance_total),
              tone: 'slate' as const,
            },
          ]
        : [],
    [detail]
  );

  return (
    <div className="admin-page-stack">
      <section className="admin-hero-card">
        <div>
          <p className="admin-section-eyebrow">Operadores</p>
          <h1>Clubs da plataforma</h1>
          <p>Lista global com drill-down operacional, financeiro e comercial por club.</p>
        </div>
        <div className="admin-topbar__actions">
          {detail ? <span className="admin-chip">Selecionado: {detail.profile.club_name}</span> : null}
          <button type="button" className="admin-secondary-button" onClick={() => void loadData(search)} disabled={loading}>
            {loading ? 'Buscando...' : 'Atualizar lista'}
          </button>
        </div>
      </section>

      <section className="admin-section-card">
        <div className="admin-toolbar">
          <input
            className="admin-search-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por club, codigo ou owner"
          />
          <button type="button" className="admin-secondary-button" onClick={() => void loadData(search)} disabled={loading}>
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
        </div>

        {error ? <div className="admin-banner admin-banner--error">{error}</div> : null}

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Club</th>
                <th>Owner</th>
                <th>Membros</th>
                <th>Mesas</th>
                <th>Rake</th>
                <th>Pagamento</th>
                <th>Assinatura</th>
                <th>Status</th>
                <th>Detalhe</th>
              </tr>
            </thead>
            <tbody>
              {clubs.length ? (
                clubs.map((club) => (
                  <tr key={club.club_id} className={selectedClubId === club.club_id ? 'admin-table__row--selected' : ''}>
                    <td>
                      <strong>{club.club_name}</strong>
                      <span>{club.club_code}</span>
                    </td>
                    <td>
                      <strong>{club.owner_name}</strong>
                      <span>{club.owner_email}</span>
                    </td>
                    <td>{club.total_members}</td>
                    <td>{club.total_tables}</td>
                    <td>{formatAmount(club.rake_total)}</td>
                    <td>{club.payment_status || '-'}</td>
                    <td>{club.subscription_status || '-'}</td>
                    <td>{club.is_active ? 'ATIVO' : 'INATIVO'}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-link-chip admin-link-chip--button"
                        onClick={() => void loadDetail(club.club_id)}
                        disabled={detailLoading && selectedClubId === club.club_id}
                      >
                        {selectedClubId === club.club_id ? (detailLoading ? 'Carregando...' : 'Recarregar') : 'Detalhar'}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9}>Nenhum club encontrado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {detailError ? <div className="admin-banner admin-banner--error">{detailError}</div> : null}

      {detail ? (
        <>
          <section className="admin-metrics-grid">
            {metricCards.map((card) => (
              <MetricCard key={card.title} {...card} />
            ))}
          </section>

          <section className="admin-grid admin-grid--two">
            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Perfil</p>
                  <h2>Identidade e contexto do club</h2>
                </div>
                <span className="admin-chip">Atualizado em {formatDateTime(detail.generated_at)}</span>
              </div>

              <div className="admin-detail-grid">
                <div className="admin-detail-pair">
                  <span>Nome</span>
                  <strong>{detail.profile.club_name}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Codigo</span>
                  <strong>{detail.profile.club_code}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Owner</span>
                  <strong>{detail.profile.owner_name}</strong>
                  <small>{detail.profile.owner_email || '-'}</small>
                </div>
                <div className="admin-detail-pair">
                  <span>Status operacional</span>
                  <strong>{detail.profile.is_active ? 'ATIVO' : 'INATIVO'}</strong>
                  <small>Publico: {formatToggle(detail.profile.is_public)}</small>
                </div>
                <div className="admin-detail-pair">
                  <span>Assinatura</span>
                  <strong>{detail.profile.subscription_status || '-'}</strong>
                  <small>{detail.profile.subscription_plan_code || 'Sem plano'}</small>
                </div>
                <div className="admin-detail-pair">
                  <span>Expira</span>
                  <strong>{formatDateTime(detail.profile.subscription_expires_at)}</strong>
                </div>
                <div className="admin-detail-pair admin-detail-pair--full">
                  <span>Descricao</span>
                  <strong>{detail.profile.description || 'Sem descricao cadastrada.'}</strong>
                </div>
              </div>
            </article>

            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Infra financeira</p>
                  <h2>Conta externa e saude operacional</h2>
                </div>
                <span className="admin-chip">{detail.profile.payment_status || 'SEM CONTA'}</span>
              </div>

              <div className="admin-detail-grid">
                <div className="admin-detail-pair">
                  <span>Provider</span>
                  <strong>{detail.profile.payment_provider || '-'}</strong>
                  <small>{detail.profile.payment_environment || '-'}</small>
                </div>
                <div className="admin-detail-pair">
                  <span>Display</span>
                  <strong>{detail.profile.payment_display_name || 'Sem nome externo'}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>PIX</span>
                  <strong>{formatToggle(detail.profile.pix_enabled)}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Saque habilitado</span>
                  <strong>{formatToggle(detail.profile.withdrawal_enabled)}</strong>
                  <small>Aprovacao manual: {formatToggle(detail.profile.withdrawals_require_manual_approval)}</small>
                </div>
                <div className="admin-detail-pair">
                  <span>Webhook</span>
                  <strong>{formatDateTime(detail.profile.last_webhook_at)}</strong>
                </div>
                <div className="admin-detail-pair">
                  <span>Ultima validacao</span>
                  <strong>{formatDateTime(detail.profile.last_validated_at)}</strong>
                  <small>Sync: {formatDateTime(detail.profile.last_sync_at)}</small>
                </div>
                <div className="admin-detail-pair admin-detail-pair--full">
                  <span>Ultimo erro</span>
                  <strong>{detail.profile.last_error_message || 'Sem erro registrado.'}</strong>
                </div>
              </div>

              <div className="admin-kpi-grid">
                <article className="admin-kpi-item">
                  <span>Membros ativos</span>
                  <strong>{detail.summary.active_members}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Admins do club</span>
                  <strong>{detail.summary.club_admins}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Saques em fila</span>
                  <strong>{formatAmount(detail.summary.pending_withdrawals_amount)}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Custo gateway 30d</span>
                  <strong>{formatAmount(detail.summary.gateway_fee_30d)}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Financeiro aberto</span>
                  <strong>{detail.summary.open_financial_entries}</strong>
                </article>
                <article className="admin-kpi-item">
                  <span>Ultima atividade</span>
                  <strong>{formatDateTime(detail.summary.last_activity_at)}</strong>
                </article>
              </div>
            </article>
          </section>

          <section className="admin-grid admin-grid--two">
            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Mesas</p>
                  <h2>Snapshot operacional</h2>
                </div>
                <span className="admin-chip">{detail.tables.length} mesa(s)</span>
              </div>

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Mesa</th>
                      <th>Jogo</th>
                      <th>Blinds</th>
                      <th>Status</th>
                      <th>Sessoes</th>
                      <th>Ultima atividade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.tables.length ? (
                      detail.tables.map((table) => (
                        <tr key={table.table_id}>
                          <td>
                            <strong>{table.table_name}</strong>
                            <span>{table.current_players}/{table.max_players} lugares</span>
                          </td>
                          <td>{table.game_type}</td>
                          <td>
                            <strong>
                              {formatAmount(table.small_blind)} / {formatAmount(table.big_blind)}
                            </strong>
                            <span>Stack {formatAmount(table.min_stack)} - {formatAmount(table.max_stack)}</span>
                          </td>
                          <td>{table.status}</td>
                          <td>{table.open_sessions}</td>
                          <td>{formatDateTime(table.last_activity_at)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>Nenhuma mesa cadastrada para este club.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Membros</p>
                  <h2>Maiores sinais de operacao</h2>
                </div>
                <span className="admin-chip">{detail.top_members.length} membro(s)</span>
              </div>

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Jogador</th>
                      <th>Papel</th>
                      <th>Carteira</th>
                      <th>Depositos 30d</th>
                      <th>Saques 30d</th>
                      <th>Ultima atividade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.top_members.length ? (
                      detail.top_members.map((member) => (
                        <tr key={member.user_id}>
                          <td>
                            <strong>{member.user_name}</strong>
                            <span>{member.email || '-'}</span>
                          </td>
                          <td>
                            <strong>{member.role}</strong>
                            <span>{member.status}</span>
                          </td>
                          <td>
                            <strong>{formatAmount(member.wallet_balance)}</strong>
                            <span>{member.open_table_sessions} sessao(oes)</span>
                          </td>
                          <td>{formatAmount(member.confirmed_deposits_30d)}</td>
                          <td>{formatAmount(member.paid_withdrawals_30d)}</td>
                          <td>{formatDateTime(member.last_activity_at)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>Nenhum membro consolidado para este club.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </article>
          </section>

          <section className="admin-grid admin-grid--two">
            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Financeiro</p>
                  <h2>Eventos recentes do club</h2>
                </div>
              </div>

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Quando</th>
                      <th>Tipo</th>
                      <th>Jogador</th>
                      <th>Status</th>
                      <th>Valor</th>
                      <th>Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.recent_finance.length ? (
                      detail.recent_finance.map((entry) => (
                        <tr key={entry.id}>
                          <td>{formatDateTime(entry.requested_at)}</td>
                          <td>
                            <strong>{entry.kind}</strong>
                            <span>{formatDateTime(entry.settled_at)}</span>
                          </td>
                          <td>{entry.user_name}</td>
                          <td>{entry.status}</td>
                          <td>{formatAmount(entry.amount_reais)}</td>
                          <td>{formatAmount(entry.fee_reais)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>Nenhum evento financeiro encontrado para este club.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="admin-section-card">
              <div className="admin-section-card__header">
                <div>
                  <p className="admin-section-eyebrow">Escopo</p>
                  <h2>O que esta etapa cobre</h2>
                </div>
              </div>

              <div className="admin-note-stack">
                {detail.notes.map((note) => (
                  <div key={note} className="admin-note-chip">
                    {note}
                  </div>
                ))}
              </div>
            </article>
          </section>
        </>
      ) : (
        <section className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Detalhe</p>
              <h2>Selecione um club para abrir o drill-down</h2>
            </div>
            <span className="admin-chip">{detailLoading ? 'Carregando detalhe' : 'Sem selecao'}</span>
          </div>

          <div className="admin-note-stack">
            <div className="admin-note-chip">A lista continua sendo a entrada principal da tela.</div>
            <div className="admin-note-chip">O detalhe abre na mesma rota, sem mudar a navegacao atual.</div>
            <div className="admin-note-chip">A etapa segue somente leitura e nao libera mutacoes administrativas.</div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ClubsAdminPage;
